import React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import useVoyagerStore from '../stores/useVoyagerStore';
import type { ConversationThread } from '../types';
import { useChatSidebar } from './chatSidebarState';
import { closeWithUndo } from './closeUndo';
import { recentMarker, useSimpleSidebar, type RecentMarker } from './simpleSidebarState';

/**
 * A thread's row in the sidebar's Recents (#765, #1503): open, the mock's marker,
 * and ··· to star, rename, close to History (with an Undo once the server has
 * closed it, `closeUndo`) or delete after confirming. Nothing is held pending:
 * there is no timer.
 */

const FOCUS = { '&:focus-visible': { outline: '2px solid var(--accent)', outlineOffset: -2 } } as const;
const rowSx = (current: boolean) => ({
  appearance: 'none', border: 0, width: '100%', display: 'flex', alignItems: 'center', gap: 1.125, textAlign: 'left',
  py: 0.875, pl: 1, pr: 4.25, borderRadius: '9px', font: 'inherit', fontSize: 14, cursor: 'pointer', minWidth: 0,
  bgcolor: current ? 'var(--accent-tint)' : 'transparent', color: current ? 'var(--accent-ink)' : 'var(--ink)', fontWeight: current ? 600 : 400,
  '&:hover': { bgcolor: current ? 'var(--accent-tint)' : 'var(--sunk)' }, ...FOCUS,
});
const simpleRowSx = (current: boolean) => ({
  appearance: 'none', border: 0, width: '100%', height: 30, display: 'flex', alignItems: 'center', gap: 1, textAlign: 'left',
  pl: 1.25, pr: 4.25, borderRadius: '7px', font: 'inherit', fontSize: 13, cursor: 'pointer', minWidth: 0,
  bgcolor: current ? 'var(--active)' : 'transparent', color: current ? 'var(--ink)' : 'var(--ink-2)',
  '&:hover': { bgcolor: current ? 'var(--active)' : 'var(--hover)', color: 'var(--ink)' }, ...FOCUS,
});
const MARKER_LABEL: Record<NonNullable<RecentMarker>, string> = {
  needs: 'Waiting for your answer', run: 'Running…', done: 'Answered while you were away', fail: 'Couldn’t be answered',
};
const markerSx = (m: NonNullable<RecentMarker>) => (m === 'run'
  ? { width: 11, height: 11, flex: 'none', borderRadius: '50%', border: '1.5px solid var(--line)', borderTopColor: 'var(--acc)', animation: 'simple-spin .8s linear infinite', '@keyframes simple-spin': { to: { transform: 'rotate(360deg)' } }, '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }
  : { width: 7, height: 7, flex: 'none', borderRadius: '50%', bgcolor: m === 'needs' ? 'var(--run)' : m === 'fail' ? 'var(--bad)' : 'var(--acc)' });
const titleSx = { flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } as const;

/** One thread's row: no kind icon, and the mock's marker — running ring, amber needs-you, teal finished while away (bold title) — from `recentMarker`. */
export function ThreadRow({ thread, onOpened }: { thread: ConversationThread; onOpened?: () => void }) {
  const current = useVoyagerStore((s) => s.currentThreadId === thread.id);
  const awayDone = useSimpleSidebar((s) => s.awayDone);
  const marker = useVoyagerStore((s) => recentMarker(s as any, thread.id, awayDone));
  const [menu, setMenu] = React.useState<HTMLElement | null>(null);
  const [renaming, setRenaming] = React.useState(false);
  // Rename starts once the menu has closed: its focus trap would otherwise take focus back from the field
  const renameNext = React.useRef(false);
  const [draft, setDraft] = React.useState(thread.title);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const open = () => {
    useVoyagerStore.setState({ objectiveSpaceId: null } as any);
    if (!current) useVoyagerStore.getState().loadThread(thread.id);
    else useVoyagerStore.setState({ currentView: 'landing' } as any);
    useChatSidebar.getState().setDrawerOpen(false);
    onOpened?.();
  };
  const finishRename = (save: boolean) => {
    setRenaming(false);
    if (save && draft.trim() && draft.trim() !== thread.title) void useVoyagerStore.getState().renameThread(thread.id, draft);
  };
  const s = useVoyagerStore.getState;
  return <Box data-testid="sidebar-row" data-thread-id={thread.id} sx={{ position: 'relative', '&:hover .row-more, & .row-more:focus-visible, & .row-more[aria-expanded="true"]': { opacity: 1 } }}>
    {renaming ? <Box sx={{ ...rowSx(current), cursor: 'text' }}>
      <Box component="input" autoFocus aria-label={`Rename ${thread.title}`} value={draft} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDraft(e.target.value)}
        onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()} onBlur={() => finishRename(true)}
        onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') finishRename(true); if (e.key === 'Escape') { setDraft(thread.title); finishRename(false); } }}
        sx={{ flex: 1, minWidth: 0, font: 'inherit', fontSize: 14, border: '1px solid var(--accent)', borderRadius: '6px', px: 0.75, py: '1px', bgcolor: 'var(--surface)', color: 'var(--ink)' }} />
    </Box> : <Box component="button" type="button" aria-current={current ? 'true' : undefined} title={thread.title} onClick={open}
      data-st={marker || undefined} sx={{ ...simpleRowSx(current), fontWeight: current ? 500 : marker === 'done' ? 600 : 400 }}>
      <Box component="span" sx={titleSx}>{thread.title}</Box>
      {marker && <Box component="span" role="status" aria-label={MARKER_LABEL[marker]} title={MARKER_LABEL[marker]} sx={markerSx(marker)} />}
    </Box>}
    {!renaming && <IconButton className="row-more" size="small" aria-label={`More for ${thread.title}`} aria-haspopup="menu" aria-expanded={!!menu}
      onClick={(e) => setMenu(e.currentTarget)}
      sx={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', opacity: current ? 1 : 0, color: 'var(--ink-3)', fontWeight: 700, fontSize: 13, letterSpacing: '1px', borderRadius: '7px', px: 0.75, py: 0.25, '@media (hover: none)': { opacity: 1 } }}>
      ···
    </IconButton>}
    {/* focus is not handed back to ···, which a rename replaces with its field */}
    <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)} disableRestoreFocus
      TransitionProps={{ onExited: () => { if (renameNext.current) { renameNext.current = false; setRenaming(true); } } }}>
      <MenuItem onClick={() => { setMenu(null); s().starThread(thread.id, !thread.starred); }}>{thread.starred ? '☆ Unstar' : '★ Star'}</MenuItem>
      <MenuItem onClick={() => { renameNext.current = true; setDraft(thread.title); setMenu(null); }}>✎ Rename</MenuItem>
      <MenuItem onClick={() => {
        setMenu(null);
        // closing stops a run, as ✕ on a tab always has; it lives in History until reopened
        void closeWithUndo(thread.id, thread.title);
      }}>✕ Close (keeps it in History)</MenuItem>
      <MenuItem sx={{ color: 'var(--bad)' }} onClick={() => {
        setMenu(null);
        setConfirmDelete(true);
      }}>Delete</MenuItem>
    </Menu>
    {/* a delete is confirmed, then sent once — as History's is; it has no Undo */}
    <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} maxWidth="xs" fullWidth>
      <DialogTitle>Delete “{thread.title}”?</DialogTitle>
      <DialogContent sx={{ color: 'var(--ink-2)', fontSize: 14 }}>The conversation and its answers are deleted. This cannot be undone.</DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
        <Button variant="contained" color="error" onClick={() => { setConfirmDelete(false); useChatSidebar.getState().setDrawerOpen(false); s().deleteThread(thread.id); }}>Delete</Button>
      </DialogActions>
    </Dialog>
  </Box>;
}
